import { useContext, useEffect } from "react";
import { LiaPlusSolid } from "react-icons/lia";
import { getManagerUrl } from "@/config";

import SearchInput from "../../../manager/components/search-input/search-input";
import Spinner from "../../../shared/components/spinner/spinner";
import TextInput from "../../../shared/components/text-input/text-input";
import AuthContext from "../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../shared/definitions/datatypes";
import useHttp from "../../../shared/hooks/use-http";
import { useHttpGet } from "../../../shared/hooks/use-httpNonTable";
import { getAllData, getData } from "../../../shared/utils/api";
import { IObject, validateLoadingStatusNew } from "../../../shared/utils/utils";
import { aggregationByType, dateOptions, IAggregation, operatorByType, stateOptions } from "./datamodel-report-utils";
import { IDatamodelReportData, IQueryFilter } from "./datamodel-report-new";


import "./datamodel-report-configuration.scss";
import { BsTrash } from "react-icons/bs";

interface IDatamodelReportSetupConfig {
  onDataChange: (newData: IDatamodelReportData) => void;
  datamodelReportData: IDatamodelReportData;
}

const DatamodelReportConfiguration = ({ 
  onDataChange, 
  datamodelReportData, 
}: IDatamodelReportSetupConfig) => {
  const hasManager = getManagerUrl() !== "";
  const authCtx = useContext(AuthContext);
  const { sendRequest: getRecord, status: getRecordStatus, data: getRecordData } = useHttpGet(getData, true, authCtx.token, "default", "platform");
  const {sendRequest: getProcessTypes, data: getProcessTypesData} = useHttp(getAllData, false, authCtx.token, "default", "manager");  
  const {sendRequest: getDomainTypes, data: getDomainTypesData} = useHttp(getAllData, false, authCtx.token, "default", "manager");  
  const {sendRequest: getUsers, data: getUsersData} = useHttp(getAllData, false, authCtx.token, "default", "platform");  

  useEffect(() => {
    if (datamodelReportData?.datamodel_type === "entity" && datamodelReportData?.datamodel_id) {
      getRecord(`datamodel/entity/${datamodelReportData?.datamodel_id}`);
    } else if (datamodelReportData?.datamodel_type === "event" && datamodelReportData?.datamodel_id) {
      getRecord(`datamodel/event/${datamodelReportData?.datamodel_id}`);
    } else if (datamodelReportData?.datamodel_type === "alertcase" && !hasManager && datamodelReportData?.datamodel_id) {
      getRecord(`datamodel/alertcase/${datamodelReportData?.datamodel_id}`);
    } else if (datamodelReportData?.datamodel_type === "alertcase" && hasManager) {
      getRecord("alertcase_columns");
    }
  }, []);

  useEffect(() => {
    if (hasManager) {
      getProcessTypes("all_process_types");
      getDomainTypes("domain_types");
      getUsers("users");
    }
  },[]);

  const isLastQueryFilterComplete = () => {
    const filters = datamodelReportData?.query_filters || [];
    if (filters.length === 0) return true;
    
    const lastFilter = filters[filters.length - 1];
    return lastFilter.field && lastFilter.op && lastFilter.value;
  };

  const isLastAggregationComplete = () => {
    const columns = datamodelReportData?.columns || [];
    if (columns.length === 0) return true;
    
    const lastColumn = columns[columns.length - 1];
    return lastColumn.field && lastColumn.agg && lastColumn.aggregator_title;
  };

  const addQueryFilter = () => {
    const data = { ...datamodelReportData };
    const newFilter = {
      field: "",
      field_type: "",
      op: "",
      value: ""
    };
    data.query_filters = [...(data.query_filters || []), newFilter];
    onDataChange(data);
  };

  const removeQueryFilter = (index: number) => {
    const data = { ...datamodelReportData };
    data.query_filters = data.query_filters?.filter((_, i) => i !== index);
    onDataChange(data);
  };

  const updateQueryFilter = (index: number, field: string, value: string) => {
    const data = { ...datamodelReportData };
    if (!data.query_filters || !data.query_filters[index]) {
      return;
    }

    data.query_filters[index] = {
      ...data.query_filters[index],
      [field]: value
    };
    onDataChange(data);
  };

  const addAggregationColumn = () => {
    const data = { ...datamodelReportData };
    const newColumn = {
      field: "",
      field_type: "",
      agg: "",
      aggregator_title: "",
      agg_datatype: ""
    };
    data.columns = [...(data.columns || []), newColumn];
    onDataChange(data);
  };

  const removeAggregationColumn = (index: number) => {
    const data = { ...datamodelReportData };
    data.columns = data.columns?.filter((_, i) => i !== index);
    onDataChange(data);
  };

  const updateAggregationColumn = (index: number, field: string, value: string) => {
    const data = { ...datamodelReportData };
    if (!data.columns || !data.columns[index]) return;

    data.columns[index] = {
      ...data.columns[index],
      [field]: value
    };
    onDataChange(data);
  };

  const renderFilterValue = (filter: IQueryFilter, index: number) => {
    let filterOptions = [];
    if (filter.field === "process_type_id") {
      filterOptions = getProcessTypesData?.map((processType: IObject) => ({ 
        id: processType.id, 
        name: processType.name 
      })) || [];
    }
    if (filter.field === "domain_type_id") {
      filterOptions = getDomainTypesData?.map((domainType: IObject) => ({ 
        id: domainType.id, 
        name: domainType.name 
      })) || [];
    }
    if (filter.field === "last_changed_by_id" || filter.field === "assigned_to") {
      filterOptions = getUsersData?.map((user: IObject) => ({ 
        id: user.id, 
        name: user.name 
      })) || [];
    }
    if (filter.field === "state") {
      filterOptions = stateOptions;
    }
    if (filter.field_type === "datetime") {
      filterOptions = dateOptions;
    }
    if (filter.field_type === "float" || filter.field_type === "integer") {
      return <TextInput
        type="number"
        label="Filter value"
        id={`filterValue-${index}`}
        required={true}
        inputKey={`filterValue-${index}`}
        handleOnChange={(selectedText: string) => {
          updateQueryFilter(index, "value", selectedText);
        }}
        placeholder="Enter filter value"
        value={filter.value}
        autofocus={false}
        defaultValue={filter.value}
      />;
    }
    if (filterOptions.length === 0 && filter.field_type === "text") {
      return <TextInput
        type="text"
        label="Filter value"
        id={`filterValue-${index}`}
        required={true}
        inputKey={`filterValue-${index}`}
        handleOnChange={(selectedText: string) => {
          updateQueryFilter(index, "value", selectedText);
        }}
        placeholder="Enter filter value"
        value={filter.value}
        autofocus={false}
        defaultValue={filter.value}
      />;
    }

    return <SearchInput
      label="Filter Value"
      handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
        updateQueryFilter(index, "value", selectedOption?.id || "");
      }}
      data={filterOptions}
      selectedOption={filter.value}
      inputKey={`filter-domain-type-${index}`}
      required={true}
    />;
  };

  return <div className="datamodelReportConfiguration">
    {validateLoadingStatusNew([getRecordStatus]) && <Spinner />}
    <SearchInput
      label={"Group by column"}
      handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
        const selectedField = getRecordData?.fields.find((field: IObject) => field.input_field_name === selectedOption?.id);
        const data = { ...datamodelReportData };
        data.group_by = selectedOption?.id || "";
        data.group_by_datatype = selectedField?.field_datatype || "";
        onDataChange(data);
      } }
      data={getRecordData?.fields.map((field: IObject) => ({ id: field.input_field_name, name: field.field_name })) || []}
      selectedOption={datamodelReportData?.group_by}
      required={true} 
      inputKey={"groupBy"}    
    />
    <div>
      <div className="datamodelReportConfiguration-header">
        <h2>Query filters</h2>
        <button
          type="button"
          onClick={addQueryFilter}
          disabled={!isLastQueryFilterComplete()}
          className="btn btn-primary datamodelReportConfiguration-addButton"
        >
          <LiaPlusSolid />
        </button>
      </div>
      <div>
        {datamodelReportData.query_filters?.map((filter, index) => (
          <div key={index}>
            <div className="datamodelReportConfiguration-header">
              <span className="datamodelReportConfiguration-headerTitle">Filter {index + 1}</span>
              <button
                type="button"
                onClick={() => removeQueryFilter(index)}
                className="btn btn-secondary"
              >
                <BsTrash/>
              </button>
            </div>
            <div className="datamodelReportConfiguration-row">
              <div className="datamodelReportConfiguration-column">
                <SearchInput
                  label={"Filter column"}
                  handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
                    const selectedFieldId = selectedOption?.id || "";
                    const matchingField = getRecordData?.fields.find(
                      (field: IObject) => field.input_field_name === selectedFieldId
                    );
                    updateQueryFilter(index, "field", selectedFieldId);
                    updateQueryFilter(index, "field_type", matchingField?.field_datatype || "");
                  }}
                  data={getRecordData?.fields.map((field: IObject) => ({ id: field.input_field_name, name: field.field_name })) || []}
                  selectedOption={filter.field}
                  inputKey={`filter-field-${index}`}
                  required={true}
                />
              </div>
              <div className="datamodelReportConfiguration-column">
                <SearchInput
                  label={"Filter operator"}
                  handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
                    updateQueryFilter(index, "op", selectedOption?.id || "");
                  }}
                  data={operatorByType[filter.field_type]}
                  selectedOption={filter.op}
                  inputKey={`filter-operator-${index}`}
                  required={true}
                />
              </div>
              <div className="datamodelReportConfiguration-column">
                {renderFilterValue(filter, index)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
    <hr />
    <div>
      <div className="datamodelReportConfiguration-header">
        <h2>Aggregations</h2>
        <button
          type="button"
          onClick={addAggregationColumn}
          disabled={!isLastAggregationComplete()}
          className="btn btn-primary datamodelReportConfiguration-addButton"
        >
          <LiaPlusSolid />
        </button>
      </div>
      <div>
        {datamodelReportData.columns?.map((column, index) => (
          <div key={index}>
            <div className="datamodelReportConfiguration-header">
              <span className="datamodelReportConfiguration-headerTitle">Aggregation {index + 1}</span>
              <button
                type="button"
                onClick={() => removeAggregationColumn(index)}
                className="btn btn-secondary"
              >
                <BsTrash/>
              </button>
            </div>
            <div className="datamodelReportConfiguration-row">
              <div className="datamodelReportConfiguration-column">
                <SearchInput
                  label={"Aggregation column"}
                  handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
                    const selectedFieldId = selectedOption?.id || "";
                    const matchingField = getRecordData?.fields.find(
                      (field: IObject) => field.input_field_name === selectedFieldId
                    );
                    updateAggregationColumn(index, "field", selectedFieldId);
                    updateAggregationColumn(index, "agg_datatype", matchingField?.field_datatype || "");
                    updateAggregationColumn(index, "field_type", matchingField?.field_datatype || "");
                  }}
                  data={getRecordData?.fields.map((field: IObject) => ({ id: field.input_field_name, name: field.field_name })) || []}
                  selectedOption={column.field}
                  inputKey={`aggregation-field-${index}`}
                  required={true}
                />
              </div>
              <div className="datamodelReportConfiguration-column">
                <SearchInput
                  label={"Aggregation operator"}
                  handleOnChange={(selectedOption: IEnumerationOption | undefined) => {
                    updateAggregationColumn(index, "agg", selectedOption?.id || "");                  
                  } }
                  data={ aggregationByType[column.agg_datatype || ""]?.map((agg: IAggregation) => ({ id: agg.id, name: agg.name })) || [] }
                  selectedOption={column.agg}
                  inputKey={`aggregation-operator-${index}`}
                  required={true} />
              </div>
              <div className="datamodelReportConfiguration-column">
                <TextInput
                  type="text"
                  label="Aggregation column title"
                  id={`aggregationValue-${index}`}
                  required={true}
                  inputKey={`aggregationValue-${index}`}
                  handleOnChange={(selectedText: string) => {
                    updateAggregationColumn(index, "aggregator_title", selectedText);
                  }}
                  placeholder="Aggregation column title"
                  value={column.aggregator_title}
                  defaultValue={column.aggregator_title}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>;
};

export default DatamodelReportConfiguration;